import { useEffect, useMemo } from "react";
import * as THREE from "three";
import type { IndoorRoomConfig } from "../../domain/indoor/indoorRoom";
import type { SaunaDimensions } from "../../engines/types";
import { resolveIndoorRoomLayout } from "./indoorRoomLayout";

/**
 * Issue 175: the room an indoor sauna stands in, drawn instead of the plot.
 * Colours are scene materials, not UI tokens: a painted wall, a concrete
 * floor and a dim room behind them, so the lit sauna stays the brightest
 * thing on screen.
 */
const ROOM_BACKGROUND = 0x1d1b19;
const ROOM_FLOOR_COLOR = 0x8b8781;
const ROOM_WALL_COLOR = 0xd8d2c8;

/** How far the floor runs past the room when a side has no wall. */
const FLOOR_MARGIN_M = 6;

/**
 * World-frame part: the background and a floor large enough to reach the
 * horizon in every camera preset. Sits just under y = 0, where the indoor
 * sauna's floor joists stand (it has no plinths).
 */
export function SceneIndoorFloor() {
	const material = useMemo(
		() =>
			new THREE.MeshStandardMaterial({
				color: ROOM_FLOOR_COLOR,
				roughness: 0.92,
				metalness: 0,
			}),
		[],
	);
	useEffect(() => () => material.dispose(), [material]);
	return (
		<>
			<color attach="background" args={[ROOM_BACKGROUND]} />
			<mesh
				name="indoor-room/floor"
				rotation={[-Math.PI / 2, 0, 0]}
				position={[0, -0.001, 0]}
				receiveShadow
			>
				<planeGeometry args={[FLOOR_MARGIN_M * 4, FLOOR_MARGIN_M * 4]} />
				<primitive object={material} attach="material" />
			</mesh>
		</>
	);
}

interface SceneIndoorWallsProps {
	dimensions: SaunaDimensions;
	indoorRoom: IndoorRoomConfig;
	roomHeightM: number;
}

/**
 * Model-frame part, mounted inside the sauna rig so the walls turn with the
 * building. Each wall is a single-sided plane facing the sauna: from inside
 * the room it reads as a wall, and when the camera orbits behind it, it is
 * back-face culled instead of blocking the view (the dollhouse cut).
 */
export function SceneIndoorWalls({
	dimensions,
	indoorRoom,
	roomHeightM,
}: SceneIndoorWallsProps) {
	const layout = useMemo(
		() => resolveIndoorRoomLayout(dimensions, indoorRoom, roomHeightM),
		[dimensions, indoorRoom, roomHeightM],
	);
	const material = useMemo(
		() =>
			new THREE.MeshStandardMaterial({
				color: ROOM_WALL_COLOR,
				roughness: 0.95,
				metalness: 0,
				side: THREE.FrontSide,
			}),
		[],
	);
	useEffect(() => () => material.dispose(), [material]);
	return (
		<group name="indoor-room/walls">
			{layout.walls.map((wall) => (
				<mesh
					key={wall.wall}
					name={`indoor-room/wall-${wall.wall}`}
					position={wall.position}
					rotation={[0, wall.rotationY, 0]}
					receiveShadow
				>
					<planeGeometry args={[wall.width, wall.height]} />
					<primitive object={material} attach="material" />
				</mesh>
			))}
		</group>
	);
}
